<template>
  <div class="page-content">
    <Titlebar :incrustation="'fa-user-circle'">
      <h1>{{ $t("watchmen.titles.list") }}</h1>
    </Titlebar>
    <UiCard>
      <div class="alert alert-info mb-4">
        <h6 class="font-weight-bold">{{ $t("watchmen.list.helper.title") }}</h6>
        <p class="mb-2">{{ $t("watchmen.list.helper.body") }}</p>
        <a :href="$t('watchmen.list.helper.linkUrl')" target="_blank">
          <i class="fa fa-external-link mr-1" aria-hidden="true"></i>{{ $t("watchmen.list.helper.linkText") }}
        </a>
      </div>

      <div
        v-if="!!watchmenToRenameCount && !!watchmenToRenameTotal"
        class="alert alert-warning mb-3 w-fit-content"
        v-html="
          $t('watchmen.list.remain_to_rename', {
            count: watchmenToRenameCount,
            total: watchmenToRenameTotal,
          })
        "
      ></div>

      <div class="d-flex mb-3">
        <Button2
          v-if="canMerge"
          :disabled="!hasDataToMerge"
          @click.native.prevent="handleMergeModal"
        >
          {{ $t("watchmen.list.merge") }}
          <span class="text-mono">({{ selectedRowsAll.length }})</span>
        </Button2>
        <Button2
          v-if="canMerge && selectedRowsAll.length"
          @click.native.prevent="handleUnselectAll"
          class="btn-light ml-3"
        >
          {{ $t("watchmen.list.unselect") }}
        </Button2>
        <Button2
          class="btn-brand ml-auto"
          @click.native.prevent="handleOpenNewWatchmanModal()"
          v-if="canCreate"
        >
          <i class="fa fa-plus mr-1" aria-hidden="true"></i>
          {{ $t("watchmen.list.add") }}
        </Button2>
      </div>

      <SuperTable
        ref="tableIdentifications"
        :tableColumns="tableColumns"
        :tableData="tableData ? tableData : []"
        @sort="handleSort"
        @filter="handleFilter"
        :loading="!tableData"
      />
    </UiCard>
    <Toolbar>
      <div class="row">
        <div class="col center">
          <Pagination ref="pagination" @change="handlePagination"></Pagination>
        </div>
      </div>
    </Toolbar>

    <BasicModal ref="modalCreate" :title="$t('watchmen.list.new')">
      <template v-slot:main>
        <WatchmanForm v-model="newWatchmanData" ref="watchmanForm" />
      </template>
      <template v-slot:footer>
        <Button2
          @click.native.prevent="handleCreateNewWatchman()"
          :disabled="!isNewWatchmanFormIsValid || isNewWatchmanFormIsSubmited"
        >
          {{ $t("watchmen.list.add") }}
        </Button2>
      </template>
    </BasicModal>

    <BasicModal ref="modalMerge" :title="$t('watchmen.list.merge')">
      <template v-slot:main>
        <FormWrapper>
          <div class="alert alert-warning">
              {{ $t("watchmen.list.merge_warning") }}
              <div class="pl-4 text-dark pb-2">
                <ChannelItem 
                  v-for="channel in listAllChannelsToMerge" 
                  :key="channel.id"
                  :channel="channel"
                  class="mt-1"
                />
              </div>
          </div>
          <WatchmanForm v-model="mergeWatchmanData" ref="watchmanForm" />
        </FormWrapper>
      </template>
      <template v-slot:footer>
        <Button2
          @click.native.prevent="handleMergeNewWatchman()"
          :disabled="!isMergeWatchmanFormIsValid"
        >
          {{ $t("watchmen.list.merge") }}
        </Button2>
      </template>
    </BasicModal>
  </div>
</template>

<script>
import axios from "axios";
import API from "@/services/API";
import Utils from "@/services/utils.js";
import Globals from "@/services/globals.js";
import Titlebar from "@/components/ui/Titlebar";
import Toolbar from "@/components/ui/Toolbar";
import Btn from "@/components/ui/Button";
import UiCard from "@/components/ui/Card";
import Pagination from "@/components/ui/Pagination";
import SuperTable from "@/components/ui/Table";
import BasicModal from "@/components/ui/BasicModal.vue";
import FormWrapper from "@/components/ui/forms/FormWrapper.vue";
import WatchmanForm from "@/components/watchmen/WatchmanForm.vue";
import Button2 from "@/components/ui/Button2.vue";
import { watchmanColumns } from "@/components/watchmen/list/columns";
import { listInChannels } from "@/components/watchmen/list-in-channels";
import ChannelItem from "@/components/watchmen/channels/ChannelItem.vue";

export default {
  name: "WatchmenList",
  components: {
    Titlebar,
    Toolbar,
    Btn,
    Pagination,
    SuperTable,
    BasicModal,
    FormWrapper,
    WatchmanForm,
    Button2,
    UiCard,
    ChannelItem
},
  data: () => ({
    canMerge: Utils.can("beepiz-merge-watchmen"),
    canCreate: useDvFlagAccess("beepiz-watchmen-can:create"),
    config: {
      perpage: Globals.perPage,
      fields: [],
      page: 1,
      filter: {
        like: [],
        equals: [],
      },
      sort: ["-system_name,+name"],
    },
    tableColumns: {
      global: {
        sortable: false,
        noItemsMsg: window.V.$i18n.t("watchmen.list.emptyTable"),
      },
      columns: watchmanColumns(window),
      rowActions: {},
    },
    tableData: null,
    selectedRows: [],
    selectedRowsIdsPreserved: [],
    newWatchmanData: {
      name: "",
      description: "",
    },
    mergeWatchmanData: {
      name: "",
      description: "",
    },
    isNewWatchmanFormIsValid: false,
    isMergeWatchmanFormIsValid: false,
    isNewWatchmanFormIsSubmited: false,
    isMergeWatchmanFormIsSubmited: false,
    watchmenToRenameCount: 0,
    watchmenToRenameTotal: 0,
  }),
  methods: {
    handlePagination: function (page) {
      this.config.page = page;
      this.selectedRowsIdsPreserved = [
        ...this.selectedRowsIdsPreserved,
        ...this.selectedRows,
      ];
      this.selectedRows = [];
      this.loadData();
    },
    handleSort: function () {
      this.config.sort = [];
      for (var colKey in this.tableColumns.columns) {
        var col = this.tableColumns.columns[colKey];
        if (col.activeSort) {
          this.config.sort.push(col.activeSort + col.key);
        }
      }
      this.loadData();
    },
    loadData: function () {
      this.tableData = null;
      axios
        .get(
          API.endpoints.watchmen.index + API.constructQueryParams(this.config)
        )
        .then((response) => {
          const data = response.data.data;
          const meta = response.data.meta;
          this.watchmenToRenameTotal = meta.pagination.total;

          data.forEach((item, index) => {
            const isFoundInSelectedRows = this.selectedRowsIdsPreserved.filter(
              (row) => row.id === item.id
            );
            if (isFoundInSelectedRows.length) {
              data[index].isRowSelected = true;
              this.selectedRowsIdsPreserved =
                this.selectedRowsIdsPreserved.filter(
                  (row) => row.id !== item.id
                );
            }
            data[index].rmProceduresCount = listInChannels(
              item.channels,
              "rmProcedures"
            ).length;
            data[index].profilesCount = listInChannels(
              item.channels,
              "profiles"
            ).length;
          });

          this.tableData = data;
          this.count = meta.pagination.total;
          this.$refs.pagination.update(meta.pagination);

          this.loadWatchmenToRename();
        })
        .catch((error) => {
          console.log(error);
        });
    },
    loadWatchmenToRename: function () {
      axios
        .get(API.endpoints.watchmen.index + "?equals=system_name:1&perpage=1")
        .then((response) => {
          this.watchmenToRenameCount = response.data.meta.pagination.total;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    handleFilter: function () {
      this.config.page = 1;
      this.config.filter.like = [];
      this.config.filter.equals = [];
      for (var colKey in this.tableColumns.columns) {
        var col = this.tableColumns.columns[colKey];
        if (col.activeFilter != "") {
          if (col.filter && col.filter.type == "text") {
            this.config.filter.like.push(col.key + ":" + col.activeFilter);
          }
          if (col.filter && col.filter.type == "list") {
            this.config.filter.equals.push(col.key + ":" + col.activeFilter);
          }
        }
      }
      this.loadData();
    },
    handleUnselectAll: function () {
      this.tableData = this.tableData.map((item) => {
        item.isRowSelected = false;
        return item;
      });
      this.selectedRowsIdsPreserved = [];
    },
    handleMergeModal: function () {
      this.$refs["modalMerge"].showModal();
    },
    handleOpenNewWatchmanModal: function () {
      this.$refs["modalCreate"].showModal();
    },
    handleCreateNewWatchman: function () {
      this.isNewWatchmanFormIsSubmited = true;
      axios
        .post(API.endpoints.watchmen.add, this.newWatchmanData)
        .then((response) => {
          this.$refs["modalCreate"].hideModal();
          this.loadData();
          if (response.data.id) {
            this.$router.push({
              name: "WatchmenForm",
              params: { id: response.data.id },
            });
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    handleMergeNewWatchman: function () {
      const ids = this.selectedRowsAll.map((row) => row.id);

      axios
        .post(API.endpoints.watchmen.merge, {
          ...this.mergeWatchmanData,
          ids: ids,
        })
        .then((response) => {
          this.$refs["modalMerge"].hideModal();
          this.loadData();
          if (response.data.id) {
            this.selectedRows = [];
            this.selectedRowsIdsPreserved = [];
            this.mergeWatchmanData = {
              name: "",
              description: "",
            };
            this.$router.push({
              name: "WatchmenForm",
              params: { id: response.data.id },
            });
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
  },
  watch: {
    newWatchmanData: {
      handler: function (val) {
        this.isNewWatchmanFormIsValid = val.name.length;
      },
      deep: true,
    },
    mergeWatchmanData: {
      handler: function (val) {
        this.isMergeWatchmanFormIsValid = val.name.length;
      },
      deep: true,
    },
    tableData: {
      handler: function (val) {
        if (val) {
          this.selectedRows = val.filter((item) => {
            return item.isRowSelected;
          });
        }
      },
      deep: true,
    },
  },
  computed: {
    selectedRowsAll: function () {
      return [...this.selectedRows, ...this.selectedRowsIdsPreserved] || [];
    },
    hasDataToMerge: function () {
      return this.selectedRowsAll.length > 1;
    },
    listAllChannelsToMerge: function () {
      const channels = [];
      this.selectedRowsAll.forEach((row) => {
        row.channels.forEach((channel) => {
          if (!channels.includes(channel)) {
            channels.push(channel);
          }
        });
      });
      return channels;
    },
  },
  created() {
    this.loadData();
    window.Vfc.$refs.loading.hide();
  },
};
</script>

<style lang="scss"></style>
